import { useCallback, useEffect, useState } from "react";
import { ChevronRight, LifeBuoy, ListChecks, Moon, Sun } from "lucide-react";
import NAV from "../theme/navConfig";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { supportTicketApi } from "../api/supportTicketApi";
import { openTodoPage, todoIconTitle, useTodoSummary } from "./todo/TodoFocus";
import { TodoFlashStyle } from "./todo/todoShared";

/**
 * TopBar — minimal: breadcrumb, dark toggle, user pill.
 *
 * Previously had a global barcode lookup search + notification bell;
 * removed Apr 29 2026 since neither was being used in the wild.
 *
 * Support tickets (Sep 2026): a life-buoy button opens Support Tickets and
 * carries the UNREAD count — tickets somebody else acted on since this user
 * last opened them (a new ticket for support and the company owner, a reply
 * for the raiser). Refreshed every 90s, on window focus, and whenever the
 * tickets page fires "support-tickets-changed". The poll is silent (no
 * progress-bar flash) and best-effort: a failed count leaves the old one.
 *
 * To-Do (Sep 2026): the first button on the right. Its badge is the number
 * of tasks that need this user (to do, waiting on them, to check); the
 * button pulses while one of them is new or overdue. Refreshed every 60s,
 * on focus, and whenever a task changes ("todo-tasks-changed").
 */

const TICKET_POLL_MS = 90_000;

export default function TopBar({ page }) {
  const { user } = useAuth();
  const { isDark, toggle } = useTheme();
  const label = NAV.flatMap(g => g.items).find(i => i.id === page)?.label || "";
  const [tickets, setTickets] = useState({ unread: 0, actionNeeded: 0 });
  const signedIn = Boolean(user);
  const { summary: todo } = useTodoSummary(signedIn);
  const todoCount = todo?.needsAction || 0;
  const todoFlash = Boolean(todo?.flash);

  const refreshTickets = useCallback(async () => {
    try {
      const res = await supportTicketApi.counts();
      const d = res.data?.data ?? res.data ?? {};
      setTickets({ unread: Number(d.unread) || 0, actionNeeded: Number(d.actionNeeded) || 0 });
    } catch {
      // Best-effort badge — keep the last count on a failed poll.
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    refreshTickets();
    const timer = setInterval(refreshTickets, TICKET_POLL_MS);
    window.addEventListener("focus", refreshTickets);
    window.addEventListener("support-tickets-changed", refreshTickets);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refreshTickets);
      window.removeEventListener("support-tickets-changed", refreshTickets);
    };
  }, [signedIn, refreshTickets]);

  const ticketTitle = [
    "Support tickets",
    tickets.unread > 0 ? `${tickets.unread} with new activity` : null,
    tickets.actionNeeded > 0
      ? `${tickets.actionNeeded} waiting on ${user?.role === "SUPER_ADMIN" ? "support" : "you"}`
      : null,
  ].filter(Boolean).join(" — ");

  return (
    <div style={{
      height: 54, background: "var(--bg-card)", borderBottom: `1px solid var(--border)`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 24px", flexShrink: 0, gap: 16,
      boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
    }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span style={{ fontSize: 14, color: "var(--text-2)" }}>GarmentERP</span>
        <ChevronRight size={12} style={{ color: "var(--text-2)" }} />
        <span style={{ fontSize: 15, fontWeight: 500, color: "var(--text-0)", fontFamily: "var(--font-ui)" }}>
          {label}
        </span>
      </div>

      {/* Right */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <TodoFlashStyle />
        {/* To-Do — tasks that need this user; pulses while one is new or overdue */}
        <button
          onClick={openTodoPage}
          title={todoIconTitle(todo)}
          className={todoFlash ? "todo-glow" : undefined}
          style={{
            position: "relative",
            background: page === "todo" ? "var(--primary-light)" : todoCount > 0 ? "var(--danger-bg)" : "var(--bg-hover)",
            border: `1px solid ${todoFlash ? "var(--danger)" : "var(--border)"}`, borderRadius: 7,
            padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            fontFamily: "inherit", fontSize: 13, fontWeight: 700,
            color: todoCount > 0 ? "var(--danger)" : "var(--text-1)",
          }}
        >
          <ListChecks size={15} />
          To-Do
          {todoCount > 0 && (
            <span className={todoFlash ? "todo-flash" : undefined} style={{
              position: "absolute", top: -6, right: -6, minWidth: 17, height: 17,
              padding: "0 4px", borderRadius: 9, background: "var(--danger)", color: "#fff",
              fontSize: 10.5, fontWeight: 700, lineHeight: "17px", textAlign: "center",
              boxShadow: "0 0 0 2px var(--bg-card)",
            }}>
              {todoCount > 99 ? "99+" : todoCount}
            </span>
          )}
        </button>

        {/* Support tickets — unread badge */}
        <button
          onClick={() => { window.location.hash = "support-tickets"; }}
          title={ticketTitle}
          style={{
            position: "relative",
            background: page === "support-tickets" ? "var(--primary-light)" : "var(--bg-hover)",
            border: `1px solid var(--border)`, borderRadius: 7,
            padding: 7, cursor: "pointer", display: "flex", transition: "border-color 0.14s",
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--border-hover)")}
          onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
        >
          <LifeBuoy size={15} style={{ color: tickets.unread > 0 ? "var(--primary)" : "var(--text-1)" }} />
          {tickets.unread > 0 && (
            <span style={{
              position: "absolute", top: -6, right: -6, minWidth: 17, height: 17,
              padding: "0 4px", borderRadius: 9, background: "var(--danger)", color: "#fff",
              fontSize: 10.5, fontWeight: 700, lineHeight: "17px", textAlign: "center",
              boxShadow: "0 0 0 2px var(--bg-card)",
            }}>
              {tickets.unread > 99 ? "99+" : tickets.unread}
            </span>
          )}
        </button>

        {/* Dark mode toggle */}
        <button onClick={toggle} title={isDark ? "Light mode" : "Dark mode"} style={{
          background: "var(--bg-hover)", border: `1px solid var(--border)`, borderRadius: 7,
          padding: 7, cursor: "pointer", display: "flex", transition: "border-color 0.14s",
        }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--border-hover)")}
          onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}>
          {isDark ? <Sun size={15} style={{ color: "var(--warn)" }} /> : <Moon size={15} style={{ color: "var(--text-1)" }} />}
        </button>

        {/* Divider */}
        <div style={{ width: 1, height: 22, background: "var(--border)" }} />

        {/* User pill */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          padding: "4px 10px 4px 4px", borderRadius: 8,
          border: `1px solid var(--border)`, background: "var(--bg-card)",
          transition: "border-color 0.14s",
        }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--border-hover)")}
          onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
        >
          <div style={{
            width: 26, height: 26, borderRadius: "50%", background: "var(--grad)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
            fontFamily: "var(--font-ui)", flexShrink: 0,
          }}>
            {user?.fullName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "??"}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-0)", lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {user?.fullName?.split(" ")[0] || "User"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)" }}>
              {user?.companyName || "SM IT Solutions"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}